/**
 * NavPanel — Rail + SlideOverPanel 기반 네비게이션 컴포넌트
 *
 * UI framework 프리미티브(ScrollArea, Sheet, Tooltip) 활용.
 * 기존 SidebarNavigation의 경로 매칭 로직은 유지.
 */
import { Link } from "@tanstack/react-router";
import { ChevronDown, Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";
import {
  ScrollArea,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@modootoday/ui-widgets";
import { cn } from "@/shared/lib/utils";
import { type NavItem } from "@/shared/lib/tool-registry";
import { getDevStatus, DEV_STATUS_META } from "@/shared/lib/dev-status";

/* ═══ Types ═══ */

export interface SidebarSection {
  key: string;
  title: string;
  icon?: React.ReactNode;
  items: NavItem[];
}

/* ═══ Path matching helpers (exported for breadcrumb) ═══ */

export function matchesPath(path: string, target: string): boolean {
  return path === target || path.startsWith(target + "/");
}

export function isItemActive(item: NavItem, path: string): boolean {
  if (item.children) {
    if (item.navigable && matchesPath(path, item.to)) return true;
    return item.children.some((child) => matchesPath(path, child.to));
  }
  return matchesPath(path, item.to);
}

export function isSectionActive(items: NavItem[], path: string): boolean {
  return items.some((item) => isItemActive(item, path));
}

function getItemTrail(item: NavItem, path: string): string[] | null {
  if (item.children) {
    if (item.navigable && matchesPath(path, item.to)) return [item.label];
    const activeChild = item.children.find((child) =>
      matchesPath(path, child.to),
    );
    if (activeChild) return [item.label, activeChild.label];
    return null;
  }
  if (matchesPath(path, item.to)) return [item.label];
  return null;
}

export function getSectionTrail(
  section: SidebarSection,
  path: string,
): string[] {
  for (const item of section.items) {
    const trail = getItemTrail(item, path);
    if (trail) return section.title ? [section.title, ...trail] : trail;
  }
  return [];
}

/* ═══ Internal sub-components ═══ */

function DevDot({ path }: { path: string }) {
  const status = getDevStatus(path);
  if (!status || status === "static") return null;
  const meta = DEV_STATUS_META[status];
  return (
    <span
      className={cn(
        "ml-auto inline-block size-[5px] shrink-0 rounded-full opacity-80",
        meta.dotClass,
      )}
      title={`${meta.shortLabel}: ${meta.description}`}
    />
  );
}

function PanelLeafItem({
  item,
  active,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "mt-px flex items-center gap-2 rounded-md px-2 py-[5px] text-[11.5px] transition-colors",
        active
          ? "bg-accent/10 font-semibold text-text-brand"
          : "text-text-tertiary hover:bg-surface-secondary/40 hover:text-text-primary",
      )}
    >
      <span
        className={cn(
          "flex size-3.5 shrink-0 items-center justify-center",
          active ? "text-text-brand" : "text-text-tertiary/50",
        )}
      >
        {item.icon}
      </span>
      <span className="flex-1 truncate">{item.label}</span>
      {item.ai && (
        <Sparkles
          className={cn(
            "size-2.5 shrink-0",
            active ? "text-text-brand/50" : "text-amber-400/60",
          )}
        />
      )}
      {!active && <DevDot path={item.to} />}
    </Link>
  );
}

function PanelGroupChildren({
  children,
  currentPath,
  onNavigate,
}: {
  children: NonNullable<NavItem["children"]>;
  currentPath: string;
  onNavigate?: () => void;
}) {
  return (
    <div className="ml-3 border-l border-stroke-subtle/50 py-px pl-2">
      {children.map((child) => {
        const childActive = matchesPath(currentPath, child.to);
        return (
          <Link
            key={child.to}
            to={child.to}
            onClick={onNavigate}
            className={cn(
              "relative block rounded px-2 py-[3px] pl-3 text-[11px] transition-colors",
              childActive
                ? "bg-accent/10 font-semibold text-text-brand shadow-[inset_2px_0_0_0_currentColor]"
                : "text-text-tertiary/70 hover:text-text-primary",
            )}
          >
            <span
              className={cn(
                "absolute left-1 top-1/2 size-1.5 -translate-y-1/2 rounded-full transition-colors",
                childActive ? "bg-accent" : "bg-border",
              )}
            />
            <span className="flex-1 truncate">{child.label}</span>
            {!childActive && <DevDot path={child.to} />}
          </Link>
        );
      })}
    </div>
  );
}

function PanelGroupItem({
  item,
  currentPath,
  groupOpen,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  currentPath: string;
  groupOpen: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  const groupActive = isItemActive(item, currentPath);
  const selfActive = item.navigable && matchesPath(currentPath, item.to);

  return (
    <div className="mt-px">
      <div
        className={cn(
          "flex items-center rounded-md transition-colors",
          groupActive
            ? "text-text-brand"
            : "text-text-tertiary hover:text-text-primary",
        )}
      >
        {item.navigable ? (
          <Link
            to={item.to}
            onClick={onNavigate}
            className={cn(
              "flex flex-1 items-center gap-2 rounded-l-md px-2 py-[5px] text-[11.5px] transition-colors",
              selfActive
                ? "bg-accent/10 font-semibold text-text-brand"
                : groupActive
                  ? "font-semibold text-text-brand"
                  : "hover:bg-surface-secondary/40",
            )}
          >
            <span
              className={cn(
                "flex size-3.5 shrink-0 items-center justify-center",
                groupActive ? "text-text-brand" : "text-text-tertiary/50",
              )}
            >
              {item.icon}
            </span>
            <span className="flex-1 truncate">{item.label}</span>
          </Link>
        ) : (
          <button
            onClick={onToggle}
            className={cn(
              "flex flex-1 items-center gap-2 px-2 py-[5px] text-left text-[11.5px] transition-colors",
              groupActive && "font-semibold",
            )}
          >
            <span
              className={cn(
                "flex size-3.5 shrink-0 items-center justify-center",
                groupActive ? "text-text-brand" : "text-text-tertiary/50",
              )}
            >
              {item.icon}
            </span>
            <span className="flex-1 truncate">{item.label}</span>
          </button>
        )}
        <button
          onClick={onToggle}
          className="flex shrink-0 items-center justify-center rounded-r-md px-1.5 py-[5px] text-text-tertiary/40 hover:text-text-tertiary"
        >
          <ChevronDown
            className={cn(
              "size-2.5 transition-transform duration-150",
              groupOpen && "rotate-180",
            )}
          />
        </button>
      </div>

      {groupOpen && item.children && (
        <PanelGroupChildren
          children={item.children}
          currentPath={currentPath}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

/* ═══ NavPanelContent — SlideOverPanel 내부 ═══ */

export interface NavPanelContentProps {
  section: SidebarSection;
  currentPath: string;
  isGroupOpen: (item: NavItem) => boolean;
  toggleGroup: (key: string, open: boolean) => void;
  onNavigate?: () => void;
}

export function NavPanelContent({
  section,
  currentPath,
  isGroupOpen,
  toggleGroup,
  onNavigate,
}: NavPanelContentProps) {
  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activeItem = nav.querySelector<HTMLElement>(
      "[data-state='active'], .bg-accent\\/10",
    );
    activeItem?.scrollIntoView({ block: "nearest" });
  }, [currentPath]);

  return (
    <ScrollArea className="flex-1">
      <div ref={navRef} className="px-3 py-2">
        {section.items.map((item) => {
          if (item.children) {
            const groupOpen = isGroupOpen(item);
            return (
              <PanelGroupItem
                key={item.label}
                item={item}
                currentPath={currentPath}
                groupOpen={groupOpen}
                onToggle={() => toggleGroup(item.label, groupOpen)}
                onNavigate={onNavigate}
              />
            );
          }
          const active = isItemActive(item, currentPath);
          return (
            <PanelLeafItem
              key={item.to}
              item={item}
              active={active}
              onNavigate={onNavigate}
            />
          );
        })}
      </div>
    </ScrollArea>
  );
}

/* ═══ MobileNavSheet — 모바일 전체 네비게이션 ═══ */

export interface MobileNavSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: SidebarSection[];
  currentPath: string;
  isGroupOpen: (item: NavItem) => boolean;
  toggleGroup: (key: string, open: boolean) => void;
}

export function MobileNavSheet({
  open,
  onOpenChange,
  sections,
  currentPath,
  isGroupOpen,
  toggleGroup,
}: MobileNavSheetProps) {
  function handleNavigate() {
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[300px] p-0">
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle className="text-sm">메뉴</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-57px)]">
          {sections.map((section) => (
            <div
              key={section.key}
              className="border-b border-stroke-subtle/30 py-2"
            >
              {section.title && (
                <div className="flex items-center gap-2 px-4 py-1.5">
                  <span className="flex size-4 items-center justify-center text-text-tertiary">
                    {section.icon}
                  </span>
                  <span className="text-[11px] font-bold text-text-tertiary">
                    {section.title}
                  </span>
                </div>
              )}
              <div className="px-2">
                <NavPanelContent
                  section={section}
                  currentPath={currentPath}
                  isGroupOpen={isGroupOpen}
                  toggleGroup={toggleGroup}
                  onNavigate={handleNavigate}
                />
              </div>
            </div>
          ))}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
